import React, { useMemo, useRef, useCallback } from 'react';
import Table from '@/components/tables/Table';
import { Renting } from '../api/rentingsApi';
import { Link, useNavigate } from 'react-router-dom';
import Badge from '@/components/ui/badge/Badge';
import Button from '@/components/ui/button/Button';
import { Modal } from '@/components/ui/modal';
import { useModal } from '@/shared/hooks/useModal';
import { useCancelRenting, useConfirmRenting, useUpdateRentingStartDateTime, useUpdateRentingEndDateTime, useUpdateRentingDeliveryAddress, useUpdateRentingPickupAddress } from '../hooks/useRentings';
import { formatDate } from '@fullcalendar/core/index.js';
import { CarWithLoadedModel } from '@/features/cars/api/carsApi';
import { useQueryClient } from '@tanstack/react-query';
import { useAllReports } from '@/features/cars/hooks/useCarReports';
import { ReportsListResponse } from '@/features/cars/api/carReportsApi';
import { useTableColumns } from '@/shared/hooks/useTableColumns';
import { carReportsApi } from '@/features/cars/api/carReportsApi';
import { Client } from '@/features/clients/api/clientsApi';
import { CopyButton } from '@/components/ui/button/CopyButton';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/toast/Toast';
import { rentingsApi } from '../api/rentingsApi';
import RentingDateTimeEditModal from './RentingDateTimeEditModal';
import RentingAddressEditModal from './RentingAddressEditModal';

interface RentingsTableProps {
  rentings: Renting[];
  cars: CarWithLoadedModel[];
  isLoading: boolean;
  onSort?: (orderBy: string) => void;
  currentOrder?: string;
  hideClientColumn?: boolean;
}

const formatPrice = (price: string, currency: string) => {
  const currencySymbols: Record<string, string> = {
    'gbp': '£',
    'eur': '€',
    'usd': '$',
  };

  const numericPrice = parseFloat(price);
  const formattedPrice = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericPrice);

  const symbol = currencySymbols[currency.toLowerCase()] || currency.toUpperCase();

  if (!['gbp', 'eur', 'usd'].includes(currency.toLowerCase())) {
    return `${formattedPrice} ${symbol}`;
  }

  // Для остальных валют символ идет перед суммой
  return `${symbol} ${formattedPrice}`;
};

const formatDeliveryDate = (date: string) => {
  return new Date(date).toLocaleDateString();
};

const formatTime = (string: string) => {
  return string.slice(0, 5);
};

const getGoogleMapsUrl = (latitude: string, longitude: string) => {
  return `https://maps.google.com/?q=${latitude},${longitude}`;
};

// Функция для проверки, можно ли редактировать дату/время
const canEditDateTime = (date: string, time: string): boolean => {
  const dateTime = new Date(`${date}T${time}`);
  const now = new Date();
  return dateTime > now;
};

const ClientCell = ({ client }: {client: Client}) => {
  if (!client) return null;

  const getBadgeProps = (status: string) => {
    switch (status) {
      case 'verified':
        return { color: 'success' as const, text: 'Verified' };
      case 'verification_needed':
        return { color: 'warning' as const, text: 'Verification Needed' };
      default:
        return { color: 'light' as const, text: 'New' };
    }
  };

  const badgeProps = getBadgeProps(client.verification_status);

  return (
    <div className="flex flex-col">
      <Link
        to={`/clients/${client.id}`}
        className="font-medium text-text-primary hover:text-brand-500 transition-colors mb-2"
        title="View client details"
      >
        {`${client.first_name} ${client.last_name}`}
      </Link>
      <div className="w-fit mb-2">
        <Badge
          color={badgeProps.color}
          variant="light"
          size="md"
        >
          {badgeProps.text}
        </Badge>
      </div>
      <a
        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(client.email)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-blue-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {client.email}
      </a>
      <span className="text-sm text-text-tertiary">{client.phone}</span>
    </div>
  );
};

export const RentingsTable: React.FC<RentingsTableProps> = ({
  rentings,
  cars,
  isLoading,
  onSort,
  currentOrder,
  hideClientColumn = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { visibleColumns } = useTableColumns();
  const { isOpen: isCancelOpen, openModal: openCancelModal, closeModal: closeCancelModal } = useModal();
  const { isOpen: isConfirmOpen, openModal: openConfirmModal, closeModal: closeConfirmModal } = useModal();
  const { isOpen: isStartDateTimeEditOpen, openModal: openStartDateTimeEditModal, closeModal: closeStartDateTimeEditModal } = useModal();
  const { isOpen: isEndDateTimeEditOpen, openModal: openEndDateTimeEditModal, closeModal: closeEndDateTimeEditModal } = useModal();
  const { isOpen: isDeliveryAddressEditOpen, openModal: openDeliveryAddressEditModal, closeModal: closeDeliveryAddressEditModal } = useModal();
  const { isOpen: isPickupAddressEditOpen, openModal: openPickupAddressEditModal, closeModal: closePickupAddressEditModal } = useModal();
  const [selectedRenting, setSelectedRenting] = React.useState<Renting | null>(null);
  const [loadingReportIds, setLoadingReportIds] = React.useState<Record<string, boolean>>({});
  const cancelRenting = useCancelRenting();
  const confirmRenting = useConfirmRenting();
  const updateStartDateTime = useUpdateRentingStartDateTime();
  const updateEndDateTime = useUpdateRentingEndDateTime();
  const updateDeliveryAddress = useUpdateRentingDeliveryAddress();
  const updatePickupAddress = useUpdateRentingPickupAddress();
  const queryClient = useQueryClient();
  const { data: allReports } = useAllReports();
  const navigate = useNavigate();
  const startDateTimeFormRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const endDateTimeFormRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const deliveryAddressFormRef = useRef<{ handleClose: () => void; isDirty: () => boolean; isValid: () => boolean } | null>(null);
  const pickupAddressFormRef = useRef<{ handleClose: () => void; isDirty: () => boolean; isValid: () => boolean } | null>(null);


  const handleCancelClick = (renting: Renting) => {
    setSelectedRenting(renting);
    openCancelModal();
  };

  const handleConfirmClick = (renting: Renting) => {
    setSelectedRenting(renting);
    openConfirmModal();
  };

  const handleStartDateTimeEditClick = (renting: Renting) => {
    setSelectedRenting(renting);
    openStartDateTimeEditModal();
  };

  const handleEndDateTimeEditClick = (renting: Renting) => {
    setSelectedRenting(renting);
    openEndDateTimeEditModal();
  };

  const handleStartDateTimeSubmit = async (data: { date: string; time: string }) => {
    if (!selectedRenting) return;
    try {
      await updateStartDateTime.mutateAsync({
        rentingId: selectedRenting.id,
        data,
      });
      closeStartDateTimeEditModal();
      setSelectedRenting(null);
    } catch (error) {
      throw error;
    }
  };

  const handleEndDateTimeSubmit = async (data: { date: string; time: string }) => {
    if (!selectedRenting) return;
    try {
      await updateEndDateTime.mutateAsync({
        rentingId: selectedRenting.id,
        data,
      });
      closeEndDateTimeEditModal();
      setSelectedRenting(null);
    } catch (error) {
      throw error;
    }
  };

  const handleDeliveryAddressEditClick = (renting: Renting) => {
    setSelectedRenting(renting);
    openDeliveryAddressEditModal();
  };

  const handlePickupAddressEditClick = (renting: Renting) => {
    setSelectedRenting(renting);
    openPickupAddressEditModal();
  };

  const handleDeliveryAddressSubmit = async (data: { address: string; latitude: string; longitude: string }) => {
    if (!selectedRenting) return;
    try {
      await updateDeliveryAddress.mutateAsync({
        rentingId: selectedRenting.id,
        data,
      });
      closeDeliveryAddressEditModal();
      setSelectedRenting(null);
    } catch (error) {
      throw error;
    }
  };

  const handlePickupAddressSubmit = async (data: { address: string; latitude: string; longitude: string }) => {
    if (!selectedRenting) return;
    try {
      await updatePickupAddress.mutateAsync({
        rentingId: selectedRenting.id,
        data,
      });
      closePickupAddressEditModal();
      setSelectedRenting(null);
    } catch (error) {
      throw error;
    }
  };

  const handleReportClick = async (renting: Renting, type: 'delivery_to_client' | 'pickup_from_client', existingReport?: any) => {
    // Создаем уникальный ключ для этой кнопки
    const buttonKey = `${renting.id}-${type}${existingReport ? `-${existingReport.id}` : ''}`;

    // Устанавливаем состояние загрузки для этой кнопки
    setLoadingReportIds(prev => ({ ...prev, [buttonKey]: true }));

    try {
      // Всегда загружаем технический профиль автомобиля
      await queryClient.fetchQuery({
        queryKey: ['car-tech-profile', renting.car.id],
        queryFn: () => carReportsApi.getTechProfile(renting.car.id)
      });

      // Если у нас есть существующий отчет, нам нужно загрузить его данные
      if (existingReport) {
        // Предварительно загружаем данные отчета
        await queryClient.fetchQuery({
          queryKey: ['report', existingReport.id],
          queryFn: () => carReportsApi.getReport(existingReport.id)
        });

        // Переходим на страницу редактирования
        navigate(`/rentings/${renting.id}/reports/${existingReport.id}/edit`);
      } else {
        // Для нового отчета загружаем существующие отчеты для проверки типов
        await queryClient.fetchQuery({
          queryKey: ['reports', renting.id],
          queryFn: async () => {
            const response = await carReportsApi.getReports();
            return response.data.filter(report => report.renting.id === renting.id);
          }
        });

        // Переходим на страницу создания репорта с указанием типа
        navigate(`/rentings/${renting.id}/reports/new?type=${type}`);
      }
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      // Сбрасываем состояние загрузки для этой кнопки
      setLoadingReportIds(prev => ({ ...prev, [buttonKey]: false }));
    }
  };

  const handleConfirmCancel = async () => {
    if (!selectedRenting) return;
    try {
      await cancelRenting.mutateAsync(selectedRenting.id);
      closeCancelModal();
      toast.success(t('rentings.cancel.success'));
    } catch (error) {
      toast.error(t('rentings.cancel.error') + `<br/><br/>${error}`);
    }
  };

  const handleConfirmRenting = async () => {
    if (!selectedRenting) return;
    try {
      await confirmRenting.mutateAsync(selectedRenting.id);
      closeConfirmModal();
      toast.success(t('rentings.confirm.success'));
    } catch (error) {
      toast.error(t('rentings.confirm.error') + `<br/><br/>${error}`);
    }
  };

  const getStatusBadgeProps = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return { color: 'success' as const, variant: 'light' as const };
      case 'draft':
        return { color: 'light' as const, variant: 'light' as const };
      case 'cancelled':
        return { color: 'error' as const, variant: 'light' as const };
      case 'pending':
        return { color: 'warning' as const, variant: 'light' as const };
      default:
        return { color: 'light' as const, variant: 'light' as const };
    }
  };

  const handleSort = (field: string) => {
    if (!onSort) return;

    // Если поле уже отсортировано по возрастанию, сортируем по убыванию
    const newOrder = currentOrder === field ? `-${field}` : field;
    onSort(newOrder);
  };

  const carsInfo = cars?.reduce((acc, car) => {
    acc[car.id] = {
      image_url: car.image_url,
      name: '',
      brand_name: '',
      model_name: ''
    };

    if (car.model) {
      acc[car.id].brand_name = car.model.brand_name;
      acc[car.id].model_name = car.model.model_name;
      acc[car.id].name = `${car.model.brand_name} ${car.model.model_name} ${car.registration_number}`;
    }
    return acc;
  }, {} as Record<string, { image_url: string, brand_name: string, model_name: string, name: string }>);

  const ReportsCell = ({ row }: { row: Renting }) => {
    const reports = row.reports
    const reportsObjects = allReports?.data?.filter((r: ReportsListResponse['data'][0]) => r.renting.id === row.id) || [];

    const reportsByActivityType = {
      delivery_to_client: reports.delivery_to_client,
      pickup_from_client: reports.pickup_from_client,
    }

    const deliveryReport = reportsByActivityType['delivery_to_client'];
    const pickupReport = reportsByActivityType['pickup_from_client'];

    // Создаем уникальные ключи для кнопок
    const deliveryButtonKey = deliveryReport ? `${row.id}-delivery_to_client-${deliveryReport}` : `${row.id}-delivery_to_client`;
    const pickupButtonKey = pickupReport ? `${row.id}-pickup_from_client-${pickupReport}` : `${row.id}-pickup_from_client`;

    // Проверяем, загружаются ли данные для этих кнопок
    const isDeliveryLoading = !!loadingReportIds[deliveryButtonKey];
    const isPickupLoading = !!loadingReportIds[pickupButtonKey];

    // Определяем, нужно ли показывать кнопки (всегда для confirmed)
    const showReportButtons = row.status.toLowerCase() === 'confirmed';

    return (
      <div className="flex flex-col gap-2">
        {showReportButtons ? (
          <>
            <Button
              variant={deliveryReport ? "success" : "outline"}
              size="sm"
              onClick={() => handleReportClick(row, 'delivery_to_client', reportsObjects.find((r: ReportsListResponse['data'][0]) => r.id === deliveryReport))}
              disabled={isDeliveryLoading}
            >
              {t('rentings.columns.reports.delivery')}
            </Button>
            <Button
              variant={pickupReport ? "success" : "outline"}
              size="sm"
              onClick={() => handleReportClick(row, 'pickup_from_client', reportsObjects.find((r: ReportsListResponse['data'][0]) => r.id === pickupReport))}
              disabled={isPickupLoading}
            >
              {t('rentings.columns.reports.pickup')}
            </Button>
          </>
        ) : (
          <div className="text-sm text-gray-500">
            —
          </div>
        )}
      </div>
    );
  };

  const columns = useMemo(() => [
    {
      key: 'id',
      header: t('rentings.columns.id'),
      field: 'id',
      headerClassName: 'w-[150px] min-w-[150px] md:w-[260px] md:min-w-auto',
      className: 'w-[150px] min-w-[150px] md:w-[260px] md:min-w-auto',
      cell: (row: Renting) => (
        <div className="relative flex flex-col gap-1" key={`car-${row.id}`}>
          <span className="text-sm text-text-tertiary">{row.id}</span>
          <CopyButton text={row.id} />
        </div>
      ),
    },
    {
      key: 'car',
      header: t('rentings.columns.car'),
      field: 'car',
      headerClassName: 'w-[150px] min-w-[150px] md:w-[260px] md:min-w-auto',
      className: 'w-[150px] min-w-[150px] md:w-[260px] md:min-w-auto',
      cell: (row: Renting) => (
        <div className="relative flex flex-col gap-1" key={`car-${row.id}`}>
          <Link
            to={`/rentings/${row.id}`}
            className="block relative"
            title={t('rentings.columns.car')}
          >
            <img
              src={carsInfo[row.car.id]?.image_url}
              alt={`${carsInfo[row.car.id]?.brand_name} ${carsInfo[row.car.id]?.model_name}`}
              className="w-full rounded-lg object-cover flex-shrink-0 border border-border-primary outline-[0px] transition-all duration-100 hover:outline hover:outline-[2px] outline-brand-400"
            />
          </Link>
        </div>
      ),
    },
    {
      key: 'delivery',
      header: t('rentings.columns.delivery'),
      field: 'delivery',
      cell: (row: Renting) => {
        const canEditStart = canEditDateTime(row.start_date, row.start_time);
        const canEditEnd = canEditDateTime(row.end_date, row.end_time);

        return (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-text-tertiary">{t('rentings.columns.delivery')}</span>
              <div className="flex items-center gap-2">
                <span className="text-lg flex items-baseline gap-1 text-text-primary">
                  {formatDeliveryDate(row.start_date)}
                  <span className="text-text-tertiary text-sm text-normal">
                    {formatTime(row.start_time)}
                  </span>
                </span>
                {canEditStart && (
                  <button
                    onClick={() => handleStartDateTimeEditClick(row)}
                    className="p-1 text-gray-800 hover:text-brand-500 transition-colors mr-[-4px]"
                    title={t('rentings.datetime_edit.edit_start_datetime')}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.50023C18.8978 2.10297 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10297 21.5 2.50023C21.8978 2.89749 22.1219 3.43705 22.1219 3.99973C22.1219 4.56241 21.8978 5.10197 21.5 5.49923L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
                {['confirmed', 'draft'].includes(row.status.toLowerCase()) && (
                  <Link
                    to={`/rent-calendar?date=${row.start_date}&highlight=${row.id}`}
                    className="flex items-center hover:text-brand-500 transition-colors relative top-0 text-text-tertiary"
                    title={t('rentings.columns.delivery')}
                    data-testid="rentings-table-delivery-calendar-button"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={getGoogleMapsUrl(row.deliver_latitude, row.deliver_longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand-500 truncate hover:text-brand-600 hover:underline w-fit max-w-[200px]"
                  title={t('rentings.columns.delivery')}
                >
                  {row.address_to_deliver}
                </a>
                {canEditStart && (
                  <button
                    onClick={() => handleDeliveryAddressEditClick(row)}
                    className="p-1 text-gray-800 hover:text-brand-500 transition-colors mr-[-4px]"
                    title={t('rentings.address_edit.edit_delivery_address')}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.50023C18.8978 2.10297 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10297 21.5 2.50023C21.8978 2.89749 22.1219 3.43705 22.1219 3.99973C22.1219 4.56241 21.8978 5.10197 21.5 5.49923L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-medium text-text-tertiary">To</span>
              <div className="flex items-center gap-2">
                <span className="text-lg flex items-baseline gap-1 text-text-primary">
                  {formatDeliveryDate(row.end_date)}
                  <span className="text-text-tertiary text-sm text-normal">
                    {formatTime(row.end_time)}
                  </span>
                </span>
                {canEditEnd && (
                  <button
                    onClick={() => handleEndDateTimeEditClick(row)}
                    className="p-1 text-gray-800 hover:text-brand-500 transition-colors"
                    title={t('rentings.datetime_edit.edit_end_datetime')}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.50023C18.8978 2.10297 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10297 21.5 2.50023C21.8978 2.89749 22.1219 3.43705 22.1219 3.99973C22.1219 4.56241 21.8978 5.10197 21.5 5.49923L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={getGoogleMapsUrl(row.pickup_latitude, row.pickup_longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand-500 truncate hover:text-brand-600 hover:underline w-fit max-w-[200px]"
                  title="Open in Google Maps"
                >
                  {row.address_to_pickup}
                </a>
                {canEditEnd && (
                  <button
                    onClick={() => handlePickupAddressEditClick(row)}
                    className="p-1 text-gray-800 hover:text-brand-500 transition-colors mr-[-4px]"
                    title={t('rentings.address_edit.edit_pickup_address')}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.50023C18.8978 2.10297 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10297 21.5 2.50023C21.8978 2.89749 22.1219 3.43705 22.1219 3.99973C22.1219 4.56241 21.8978 5.10197 21.5 5.49923L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: t('rentings.columns.status'),
      field: 'status',
      cell: (row: Renting) => (
        <Badge {...getStatusBadgeProps(row.status)} size="md">
          {t(`rentings.filters.status.${row.status.toLowerCase()}`)}
        </Badge>
      ),
      sortable: true,
      onSort: () => handleSort('status')
    },
    ...(hideClientColumn ? [] : [{
      key: 'client',
      header: t('rentings.columns.client'),
      field: 'client',
      cell: (row: Renting) => <ClientCell client={row.client} />,
    }]),
    {
      key: 'price',
      header: t('rentings.columns.price'),
      field: 'total_price',
      cell: (row: Renting) => (
        <span className="font-medium text-text-primary">
          {formatPrice(row.total_price, row.currency)}
        </span>
      ),
    },
    {
      key: 'comments',
      header: t('rentings.columns.comments'),
      field: 'comments',
      cell: (row: any) => (
        <div>
          {row.comments_count > 0 ? (
            <Link
              to={`/rentings/${row.id}#comments`}
              className="text-brand-500 hover:text-brand-600 transition-colors"
            >
              {row.comments_count} {row.comments_count === 1 ? t('comment') : t('comments')}
            </Link>
          ) : (
            <span className="text-text-tertiary text-sm">{t('No comments')}</span>
          )}
        </div>
      ),
    },
    {
      key: 'reports',
      header: t('rentings.columns.reports'),
      field: 'reports',
      cell: (row: Renting) => <ReportsCell row={row} />,
    },
    {
      key: 'actions',
      header: t('rentings.columns.actions'),
      field: 'actions',
      headerClassName: 'text-left',
      className: 'text-left',
      cell: (row: Renting) => (
        <div className="flex flex-col items-stretch gap-2">
          {row.status === 'draft' && (
            <Button
              variant="success"
              size="sm"
              onClick={() => handleConfirmClick(row)}
            >
              {t('Confirm')}
            </Button>
          )}
          <Link to={`/rentings/${row.id}`} className="flex">
            <Button variant="primary" size="sm" className="w-full">
              {t('Details')}
            </Button>
          </Link>
          {['draft'].includes(row.status.toLowerCase()) && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleCancelClick(row)}
            >
              {t('Cancel')}
            </Button>
          )}
          {row.status.toLowerCase() === 'confirmed' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                window.open(`${import.meta.env.VITE_API_HOST}/api/v1/test/rentings/render_test_pdf/${row.id || ''}`, '_blank');
              }}
            >
              {t('Invoice')}
            </Button>
          )}
        </div>
      ),
    },
  ].filter(Boolean), [t, handleSort, hideClientColumn]);

  // Фильтруем столбцы на основе visibleColumns из хука
  const filteredColumns = useMemo(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      return columns.filter(col => visibleColumns.includes(col.key));
    }
    return columns;
  }, [columns, visibleColumns]);

  return (
    <>
      <Table
        columns={filteredColumns}
        data={rentings}
        loading={isLoading}
      />


      <Modal
        isOpen={isCancelOpen}
        onClose={closeCancelModal}
        className="max-w-[400px] p-6 bg-bg-card"
      >
        <div className="text-center">
          <h3 className="mb-4 text-xl font-semibold text-text-primary">
            {t("Cancel Renting")}
          </h3>
          {selectedRenting && (
            <img
              src={carsInfo[selectedRenting.car.id].image_url}
              alt={`${carsInfo[selectedRenting.car.id].brand_name} ${carsInfo[selectedRenting.car.id].model_name}`}
              className="w-full max-w-[250px] mx-auto my-8 rounded-lg object-cover flex-shrink-0 border border-border-primary"
            />
          )}
          <p className="mb-6 text-text-tertiary">
            {t("Are you sure you want to cancel this renting?")}
            <br />
            {selectedRenting?.car.model.brand_name} {selectedRenting?.car.model.model_name} {selectedRenting?.car.registration_number}
            {" "}
            {formatDate(selectedRenting?.start_date + ' ' + selectedRenting?.start_time, {month: 'short', day: 'numeric', year: 'numeric'}) + ' — ' + formatDate(selectedRenting?.end_date + ' ' + selectedRenting?.end_time, {month: 'short', day: 'numeric', year: 'numeric'})}
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" size="sm" onClick={closeCancelModal}>
              {t("No, keep it")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleConfirmCancel}
              disabled={cancelRenting.isPending}
            >
              {cancelRenting.isPending ? t("Canceling...") : t("Yes, cancel it")}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isConfirmOpen}
        onClose={closeConfirmModal}
        className="max-w-[400px] p-6 bg-bg-card"
      >
        <div className="text-center">
          <h3 className="mb-4 text-xl font-semibold text-text-primary">
            {t("Confirm Renting")}
          </h3>
          {selectedRenting && (
            <img
              src={carsInfo[selectedRenting.car.id].image_url}
              alt={`${carsInfo[selectedRenting.car.id].brand_name} ${carsInfo[selectedRenting.car.id].model_name}`}
              className="w-full max-w-[250px] mx-auto my-8 rounded-lg object-cover flex-shrink-0 border border-border-primary"
            />
          )}
          <p className="mb-6 text-text-tertiary">
            {t("Are you sure you want to confirm this renting?")}
            <br />
            {selectedRenting?.car.model.brand_name} {selectedRenting?.car.model.model_name} {selectedRenting?.car.registration_number}
            {" "}
            {formatDate(selectedRenting?.start_date + ' ' + selectedRenting?.start_time, {month: 'short', day: 'numeric', year: 'numeric'}) + ' — ' + formatDate(selectedRenting?.end_date + ' ' + selectedRenting?.end_time, {month: 'short', day: 'numeric', year: 'numeric'})}
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" size="sm" onClick={closeConfirmModal}>
              {t("No, cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirmRenting}
              disabled={confirmRenting.isPending}
            >
              {confirmRenting.isPending ? t("Confirming...") : t("Yes, confirm it")}
            </Button>
          </div>
        </div>
      </Modal>

      <RentingDateTimeEditModal
        isOpen={isStartDateTimeEditOpen}
        onClose={closeStartDateTimeEditModal}
        onSubmit={handleStartDateTimeSubmit}
        type="start"
        currentDate={selectedRenting?.start_date || ''}
        currentTime={selectedRenting?.start_time || ''}
      />

      <RentingDateTimeEditModal
        isOpen={isEndDateTimeEditOpen}
        onClose={closeEndDateTimeEditModal}
        onSubmit={handleEndDateTimeSubmit}
        type="end"
        currentDate={selectedRenting?.end_date || ''}
        currentTime={selectedRenting?.end_time || ''}
      />

      <RentingAddressEditModal
        ref={deliveryAddressFormRef}
        isOpen={isDeliveryAddressEditOpen}
        onClose={closeDeliveryAddressEditModal}
        onSubmit={handleDeliveryAddressSubmit}
        type="delivery"
        currentAddress={selectedRenting?.address_to_deliver || ''}
        currentLatitude={selectedRenting?.deliver_latitude || ''}
        currentLongitude={selectedRenting?.deliver_longitude || ''}
      />

      <RentingAddressEditModal
        ref={pickupAddressFormRef}
        isOpen={isPickupAddressEditOpen}
        onClose={closePickupAddressEditModal}
        onSubmit={handlePickupAddressSubmit}
        type="pickup"
        currentAddress={selectedRenting?.address_to_pickup || ''}
        currentLatitude={selectedRenting?.pickup_latitude || ''}
        currentLongitude={selectedRenting?.pickup_longitude || ''}
      />
    </>
  );
};
